import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Fade,
  Avatar,
} from '@mui/material';
import {
  Vaccines as VaccinesIcon,
  LocalHospital as LocalHospitalIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  PhotoCamera as PhotoCameraIcon,
  ChildCare as ChildCareIcon,
  Psychology as PsychologyIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const quickActions = [
    {
      title: 'Вакцинация',
      description: 'Календарь прививок и график вакцинации',
      icon: <VaccinesIcon sx={{ fontSize: 32, color: '#be185d' }} />,
      path: '/vaccination',
      gradient: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
      bgColor: '#fce7f3',
      category: 'Профилактика',
    },
    {
      title: 'Неотложная помощь',
      description: 'Протоколы экстренной помощи детям',
      icon: <LocalHospitalIcon sx={{ fontSize: 32, color: '#ef4444' }} />,
      path: '/emergency',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
      bgColor: '#fef2f2',
      category: 'Экстренная помощь',
    },
    {
      title: 'Дети до года',
      description: 'Развитие, уход и вскармливание',
      icon: <ChildCareIcon sx={{ fontSize: 32, color: '#22c55e' }} />,
      path: '/children-up-to-year',
      gradient: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)',
      bgColor: '#f0fdf4',
      category: 'Педиатрия',
    },
    {
      title: 'Атлас заболеваний',
      description: 'Фотографии сыпи и описания заболеваний',
      icon: <PhotoCameraIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />,
      path: '/atlas',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
      bgColor: '#faf5ff',
      category: 'Диагностика',
    },
    {
      title: 'Графики роста',
      description: 'Нормы ВОЗ и CDC по росту и весу',
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#22c55e' }} />,
      path: '/growth',
      gradient: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)',
      bgColor: '#f0fdf4',
      category: 'Антропометрия',
    },
    {
      title: 'Калькулятор доз',
      description: 'Расчет лекарственных препаратов',
      icon: <CalculateIcon sx={{ fontSize: 32, color: '#f59e0b' }} />,
      path: '/calculator',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
      bgColor: '#fffbeb',
      category: 'Фармакология',
    },
    {
      title: 'ИИ-помощник',
      description: 'Расшифровка анализов и исследований',
      icon: <PsychologyIcon sx={{ fontSize: 32, color: '#6366f1' }} />,
      path: '/ai-assistant',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      bgColor: '#f0f9ff',
      category: 'Искусственный интеллект',
    },
    {
      title: 'Приказы и нормативы',
      description: 'Профилактические осмотры по возрасту',
      icon: <DescriptionIcon sx={{ fontSize: 32, color: '#6b7280' }} />,
      path: '/orders',
      gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
      bgColor: '#f9fafb',
      category: 'Нормативы',
    },
    {
      title: 'Клинические рекомендации',
      description: 'Официальные рекомендации Минздрава России',
      icon: <DescriptionIcon sx={{ fontSize: 32, color: '#3b82f6' }} />,
      path: '/clinical-recommendations',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
      bgColor: '#eff6ff',
      category: 'Нормативы',
    },
  ];


  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        mb: 6, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: 4,
        p: 4,
        color: '#1f2937',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h1" sx={{ 
            mb: 2, 
            fontWeight: 700,
            color: '#1f2937',
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}>
            Добро пожаловать в педиатрический справочник!
          </Typography>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            color: '#6b7280',
            fontWeight: 400,
            maxWidth: 800,
            fontSize: { xs: '1rem', md: '1.25rem' }
          }}>
            Комплексный инструмент для педиатров с актуальной информацией по вакцинации, 
            диагностике, лечению и уходу за детьми всех возрастов.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="Российские стандарты" 
              sx={{ 
                backgroundColor: '#fce7f3', 
                color: '#be185d',
                fontWeight: 600,
                border: '1px solid #f9a8d4'
              }} 
            />
            <Chip 
              label="Актуальные данные" 
              sx={{ 
                backgroundColor: '#f0fdf4', 
                color: '#22c55e',
                fontWeight: 600,
                border: '1px solid #86efac'
              }} 
            />
            <Chip 
              label="Мобильная версия" 
              sx={{ 
                backgroundColor: '#f0f9ff', 
                color: '#6366f1',
                fontWeight: 600,
                border: '1px solid #a5b4fc'
              }} 
            />
          </Box>
        </Box>
        <Box sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fce7f3 0%, #f9a8d4 100%)',
          opacity: 0.3,
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #86efac 100%)',
          opacity: 0.3,
          zIndex: 1
        }} />
      </Box>


      {/* Main Sections Grid */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
          Основные разделы
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 3
        }}>
          {quickActions.map((action, index) => (
            <Fade in={true} timeout={400 + index * 100} key={index}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  borderRadius: 4,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  background: '#ffffff',
                  border: `1px solid #e5e7eb`,
                  transition: 'all 0.3s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                    '& .gradient-overlay': {
                      opacity: 0.1,
                    },
                    '& .action-icon': {
                      transform: 'scale(1.05)',
                    }
                  },
                }}
                onClick={() => navigate(action.path)}
              >
                <Box 
                  className="gradient-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: action.gradient,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    zIndex: 1
                  }}
                />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      className="action-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        backgroundColor: action.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Chip 
                        label={action.category} 
                        size="small" 
                        sx={{ 
                          backgroundColor: action.bgColor,
                          color: action.icon.props.sx.color,
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }} 
                      />
                    </Box>
                  </Box>
                  <Typography variant="h5" sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    mb: 3,
                    lineHeight: 1.5
                  }}>
                    {action.description}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<PlayIcon />}
                    sx={{
                      backgroundColor: action.icon.props.sx.color,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        backgroundColor: action.icon.props.sx.color,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    Открыть раздел
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>
      </Box>

      {/* Quick Links Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
          Полезные ресурсы
        </Typography>
        <Card sx={{ 
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          background: '#ffffff',
          border: '1px solid #e5e7eb'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3
            }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<VaccinesIcon />}
                sx={{ 
                  justifyContent: 'flex-start', 
                  textAlign: 'left',
                  p: 2,
                  borderRadius: 3,
                  borderColor: '#be185d',
                  color: '#be185d',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#fce7f3',
                    borderColor: '#be185d',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(190, 24, 93, 0.2)'
                  }
                }}
                href="https://www.rki.de/DE/Content/Infekt/Impfen/Impfkalender/Impfkalender_node.html"
                target="_blank"
              >
                Календарь прививок RKI
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<TrendingUpIcon />}
                sx={{ 
                  justifyContent: 'flex-start', 
                  textAlign: 'left',
                  p: 2,
                  borderRadius: 3,
                  borderColor: '#22c55e',
                  color: '#22c55e',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#f0fdf4',
                    borderColor: '#22c55e',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
                  }
                }}
                href="https://www.who.int/ru/news-room/fact-sheets/detail/immunization-coverage"
                target="_blank"
              >
                ВОЗ - Иммунизация
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<HospitalIcon />}
                sx={{ 
                  justifyContent: 'flex-start', 
                  textAlign: 'left',
                  p: 2,
                  borderRadius: 3,
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                    borderColor: '#ef4444',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                  }
                }}
                href="https://www.cdc.gov/vaccines/schedules/hcp/child-adolescent.html"
                target="_blank"
              >
                CDC - График вакцинации
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
